import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { goToSection } from '../utils/navigation';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '../types';
import { Colors, Fonts, Spacing, LAYOUT } from '../constants';
import { useUnreadCountContext, useUser } from '../contexts';

/** Screens that own a bottom tab (the five main destinations) */
export type TabScreen = 'Home' | 'Browse' | 'ConversationsList' | 'Clubs' | 'Profile';

interface TabBarProps {
  /** Which tab is highlighted */
  current: TabScreen;
}

type Nav = NativeStackNavigationProp<RootStackParamList>;

interface TabItem {
  screen: keyof RootStackParamList;
  icon: keyof typeof Ionicons.glyphMap;
  iconActive: keyof typeof Ionicons.glyphMap;
  labelKey: string;
}

const MEMBER_TABS: TabItem[] = [
  { screen: 'Home', icon: 'home-outline', iconActive: 'home', labelKey: 'navigation.home' },
  { screen: 'Browse', icon: 'search-outline', iconActive: 'search', labelKey: 'navigation.browse' },
  { screen: 'ConversationsList', icon: 'chatbubbles-outline', iconActive: 'chatbubbles', labelKey: 'navigation.conversations' },
  { screen: 'Clubs', icon: 'people-outline', iconActive: 'people', labelKey: 'navigation.clubs' },
  { screen: 'Profile', icon: 'person-outline', iconActive: 'person', labelKey: 'navigation.profile' },
];

// Guests only have the public marketplace; the other slots send them to auth.
const GUEST_TABS: TabItem[] = [
  { screen: 'Browse', icon: 'search-outline', iconActive: 'search', labelKey: 'navigation.browse' },
  { screen: 'Login', icon: 'log-in-outline', iconActive: 'log-in', labelKey: 'navigation.login' },
  { screen: 'Register', icon: 'person-add-outline', iconActive: 'person-add', labelKey: 'navigation.register' },
];

/**
 * Mobile bottom tab bar (design v2).
 *
 * White, hairline top, filled Bosgroen icon when active, accent-filled
 * (`Colors.primary`) unread count on Messages. Replaces the tab row that used to live inside AppHeader.
 * Scrolling content above it needs `LAYOUT.tabBar.contentPadding` bottom
 * padding so the last item clears the bar.
 */
export const TabBar: React.FC<TabBarProps> = ({ current }) => {
  const { t } = useTranslation();
  const navigation = useNavigation<Nav>();
  const { user } = useUser();
  const { unreadCount } = useUnreadCountContext();
  const insets = useSafeAreaInsets();

  const tabs = user ? MEMBER_TABS : GUEST_TABS;
  const safeBottom = Platform.OS === 'web' ? Spacing.sm : Math.max(insets.bottom, Spacing.sm);

  return (
    <View style={[styles.container, { paddingBottom: safeBottom }]}>
      {tabs.map((tab) => {
        const isActive = current === tab.screen;
        const showUnread = tab.screen === 'ConversationsList' && unreadCount > 0;
        return (
          <TouchableOpacity
            key={tab.screen}
            style={styles.tab}
            onPress={() => {
              if (!isActive) goToSection(navigation, tab.screen);
            }}
            accessibilityRole="button"
            accessibilityState={{ selected: isActive }}
            accessibilityLabel={t(tab.labelKey)}
            activeOpacity={0.7}
          >
            <View>
              <Ionicons
                name={isActive ? tab.iconActive : tab.icon}
                size={24}
                color={isActive ? Colors.textDark : Colors.textLight}
              />
              {showUnread && (
                <View style={styles.unreadBadge}>
                  <Text style={styles.unreadText}>{unreadCount > 99 ? '99+' : unreadCount}</Text>
                </View>
              )}
            </View>
            <Text style={[styles.label, isActive && styles.labelActive]} numberOfLines={1}>
              {t(tab.labelKey)}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'flex-start',
    backgroundColor: Colors.white,
    borderTopWidth: 1,
    borderTopColor: Colors.borderLight,
    paddingTop: Spacing.sm + 2,
    paddingHorizontal: Spacing.sm,
    minHeight: LAYOUT.tabBar.height,
    zIndex: 30,
  },
  tab: {
    alignItems: 'center',
    gap: Spacing.xs,
    minWidth: 56,
    flex: 1,
  },
  label: {
    fontFamily: Fonts.semiBold,
    fontSize: 10,
    lineHeight: 12,
    color: Colors.textLight,
  },
  labelActive: {
    color: Colors.textDark,
  },
  unreadBadge: {
    position: 'absolute',
    top: -3,
    right: -10,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: Colors.primary,
    paddingHorizontal: Spacing.xs,
    justifyContent: 'center',
    alignItems: 'center',
  },
  unreadText: {
    fontFamily: Fonts.bold,
    fontSize: 10,
    lineHeight: 12,
    color: Colors.white,
  },
});
